const OPEN_BRACE = 0x7b; // {
const CLOSE_BRACE = 0x7d; // }
const OPEN_BRACKET = 0x5b; // [
const CLOSE_BRACKET = 0x5d; // ]
const QUOTE = 0x22; // "
const BACKSLASH = 0x5c; // backslash
const COMMA = 0x2c; // ,

const decoder = new TextDecoder();
const encoder = new TextEncoder();

const DEFAULT_CHUNK_SIZE = 5000;

export interface JsonArrayStreamOptions {
  onProgress?: (count: number) => void;
  /** Called with each freshly-parsed batch of elements, in document order. */
  onChunk?: (rows: any[]) => void | Promise<void>;
  /** Elements per batch/yield. Defaults to 5000. */
  chunkSize?: number;
  /** When false, elements are only delivered via onChunk and the returned array stays empty. Defaults to true. */
  accumulate?: boolean;
  /** Checked at each yield point; when it returns true, parsing stops early and returns what's been accumulated so far. */
  shouldAbort?: () => boolean;
}

/**
 * Extracts the array found at `path` inside a large JSON document without ever
 * decoding the whole buffer into a single JS string (V8 caps string length at
 * ~512MiB, which multi-hundred-MB export payloads exceed). Only the small
 * envelope around the array and each individual array element get decoded.
 *
 * Runs in slices with periodic setTimeout yields so a multi-hundred-MB payload
 * doesn't block the UI thread for the whole parse.
 */
export async function extractJsonArrayAtPath(buffer: ArrayBuffer, path: string[], options: JsonArrayStreamOptions = {}): Promise<any[]> {
  const bytes = new Uint8Array(buffer);
  let pos = skipBom(bytes, 0);
  pos = skipWhitespace(bytes, pos);

  for (const key of path) {
    if (bytes[pos] !== OPEN_BRACE) return [];
    const valueStart = scanObjectForKey(bytes, pos, encoder.encode(key));
    if (valueStart == null) return [];
    pos = skipWhitespace(bytes, valueStart);
  }

  if (bytes[pos] !== OPEN_BRACKET) return [];
  return extractArrayElements(bytes, pos, options);
}

function scanObjectForKey(bytes: Uint8Array, pos: number, targetKey: Uint8Array): number | null {
  pos = skipWhitespace(bytes, pos + 1); // skip '{'
  if (bytes[pos] === CLOSE_BRACE) return null;

  while (true) {
    pos = skipWhitespace(bytes, pos);
    const keyStart = pos + 1;
    pos = skipString(bytes, pos);
    const keyEnd = pos - 1;
    pos = skipWhitespace(bytes, pos);
    pos = skipWhitespace(bytes, pos + 1); // skip ':'
    const valueStart = pos;

    if (bytesEqual(bytes, keyStart, keyEnd, targetKey)) return valueStart;

    pos = skipValue(bytes, pos);
    pos = skipWhitespace(bytes, pos);
    if (bytes[pos] === COMMA) {
      pos++;
    } else {
      return null; // '}' or malformed - key not found in this object
    }
  }
}

async function extractArrayElements(bytes: Uint8Array, pos: number, options: JsonArrayStreamOptions): Promise<any[]> {
  const { onProgress, onChunk, chunkSize = DEFAULT_CHUNK_SIZE, accumulate = true, shouldAbort } = options;
  const rows: any[] = [];
  let pending: any[] = [];
  let count = 0;

  const flush = async (): Promise<void> => {
    onProgress?.(count);
    if (onChunk && pending.length > 0) {
      await onChunk(pending);
      pending = [];
    }
  };

  pos = skipWhitespace(bytes, pos + 1); // skip '['
  if (bytes[pos] === CLOSE_BRACKET) {
    await flush();
    return rows;
  }

  while (true) {
    const elemStart = pos;
    pos = skipValue(bytes, pos);
    const value = JSON.parse(decoder.decode(bytes.subarray(elemStart, pos)));
    count++;
    if (accumulate) rows.push(value);
    if (onChunk) pending.push(value);

    if (count % chunkSize === 0) {
      await flush();
      if (shouldAbort?.()) return rows;
      await new Promise<void>(resolve => setTimeout(resolve, 0));
    }

    pos = skipWhitespace(bytes, pos);
    if (bytes[pos] === COMMA) {
      pos = skipWhitespace(bytes, pos + 1);
    } else {
      await flush();
      return rows; // ']' or malformed
    }
  }
}

function skipValue(bytes: Uint8Array, pos: number): number {
  const b = bytes[pos];
  if (b === QUOTE) return skipString(bytes, pos);
  if (b === OPEN_BRACE || b === OPEN_BRACKET) return skipContainer(bytes, pos);

  // number / true / false / null - scan until a structural byte or whitespace
  while (pos < bytes.length) {
    const c = bytes[pos];
    if (c === COMMA || c === CLOSE_BRACE || c === CLOSE_BRACKET || isWhitespace(c)) break;
    pos++;
  }
  return pos;
}

function skipContainer(bytes: Uint8Array, pos: number): number {
  let depth = 1;
  pos++;
  while (depth > 0) {
    if (pos >= bytes.length) throw new Error('Unexpected end of buffer while parsing JSON (truncated response?)');
    const b = bytes[pos];
    if (b === QUOTE) {
      pos = skipString(bytes, pos);
      continue;
    }
    if (b === OPEN_BRACE || b === OPEN_BRACKET) depth++;
    else if (b === CLOSE_BRACE || b === CLOSE_BRACKET) depth--;
    pos++;
  }
  return pos;
}

function skipString(bytes: Uint8Array, pos: number): number {
  pos++; // skip opening quote
  while (true) {
    if (pos >= bytes.length) throw new Error('Unexpected end of buffer while parsing JSON (truncated response?)');
    const b = bytes[pos];
    if (b === BACKSLASH) {
      pos += 2;
      continue;
    }
    pos++;
    if (b === QUOTE) return pos;
  }
}

function skipBom(bytes: Uint8Array, pos: number): number {
  if (bytes[pos] === 0xef && bytes[pos + 1] === 0xbb && bytes[pos + 2] === 0xbf) return pos + 3;
  return pos;
}

function skipWhitespace(bytes: Uint8Array, pos: number): number {
  while (isWhitespace(bytes[pos])) pos++;
  return pos;
}

function isWhitespace(b: number): boolean {
  return b === 0x20 || b === 0x09 || b === 0x0a || b === 0x0d;
}

function bytesEqual(bytes: Uint8Array, start: number, end: number, target: Uint8Array): boolean {
  if (end - start !== target.length) return false;
  for (let i = 0; i < target.length; i++) {
    if (bytes[start + i] !== target[i]) return false;
  }
  return true;
}
